import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBullhorn,
    faUsers,
    faChartLine,
    faRobot,
    faVideo,
    faImage,
    faRocket,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import Navigate from '../../../../util/Navigate';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import IndieSubmenu from '../../component/section/publishers/IndieSubmenu';

function PublisherAnalyticsPage() {
    const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

    // State for handling enlarged images in a lightbox
    const [selectedImage, setSelectedImage] = useState(null);

    // Opens the modal with the selected image
    const openModal = (imageSrc) => {
        setSelectedImage(imageSrc);
    };

    // Closes the modal
    const closeModal = () => {
        setSelectedImage(null);
    };

    return (
        <>
            <Helmet prioritizeSeoTags>
                <meta charSet="utf-8" />
                <title>In-Depth Reports & Analytics for Your Game | Glitch Gaming</title>
                <meta
                    name="description"
                    content="Elevate your game's success with powerful data insights. Our advanced analytics platform consolidates your Steam stats, retention metrics, and marketing funnel data—helping you grow wishlists, refine engagement, and boost sales. Start for free now with Glitch Gaming."
                />
                <meta name="robots" content="index, follow" />
                <meta
                    property="og:title"
                    content="In-Depth Reports & Analytics for Your Game | Glitch Gaming"
                />
                <meta
                    property="og:description"
                    content="Elevate your game's success with powerful data insights. Our advanced analytics platform consolidates your Steam stats, retention metrics, and marketing funnel data—helping you grow wishlists, refine engagement, and boost sales."
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={currentUrl} />
                <meta
                    property="og:image"
                    content="https://www.glitch.fun/assets/images/publishers/game_analytics.webp"
                />
            </Helmet>

            <Header />

            {/* Lightbox Modal */}
            {selectedImage && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                    onClick={closeModal}
                >
                    <img
                        src={selectedImage}
                        alt="Enlarged"
                        style={{
                            maxWidth: '90%',
                            maxHeight: '90%',
                            border: '2px solid #fff',
                            borderRadius: '5px'
                        }}
                    />
                </div>
            )}

            {/* Hero Section */}
            <section className="analytics-hero">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 text-center text-lg-start">
                            <h1 className="display-4 fw-bold mb-4 pt-5">
                                In-Depth Reports <span className="text-gradient">& Analytics</span> for Your Game
                            </h1>
                            <p className="lead mb-5">Analyze every aspect of your game's performance to make data-driven decisions that guide your next creative move.</p>
                            <div className="cta-group">
                                <Link to={Navigate.publishersOnboardingStep1Page()} className="btn btn-primary btn-lg me-3 mb-3">
                                    Get Started Free <FontAwesomeIcon icon={faRocket} className="ms-2" />
                                </Link>
                                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                                    Book Demo
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6 pt-5">
                            <div className="hero-visual mt-5 mt-lg-0">
                                <img
                                    src="/assets/images/publishers/game_analytics.webp"
                                    alt="Analytics Dashboard"
                                    className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Value Propositions */}
            <section className="value-propositions py-5 text-black">
                <div className="container">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <div className="value-card h-100">
                                <div className="icon-wrapper bg-primary">
                                    <FontAwesomeIcon icon={faChartLine} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Comprehensive Steam Analytics</h3>
                                <ul className="value-list">
                                    <li>Wishlist conversion tracking</li>
                                    <li>Sales correlation analysis</li>
                                    <li>UTM campaign performance</li>
                                    <li>Real-time data updates</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100">
                                <div className="icon-wrapper bg-success">
                                    <FontAwesomeIcon icon={faUsers} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Player Retention Metrics</h3>
                                <ul className="value-list">
                                    <li>Daily/Weekly/Monthly active users</li>
                                    <li>Day 1/7/15/30 retention</li>
                                    <li>Playtime analysis</li>
                                    <li>Churn rate prediction</li>
                                </ul>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <div className="value-card h-100">
                                <div className="icon-wrapper bg-danger">
                                    <FontAwesomeIcon icon={faBullhorn} className="fa-2x" />
                                </div>
                                <h3 className='text-black'>Marketing Funnel Insights</h3>
                                <ul className="value-list">
                                    <li>Awareness to conversion tracking</li>
                                    <li>Social media growth metrics</li>
                                    <li>Campaign ROI analysis</li>
                                    <li>Community engagement stats</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Steam Analytics Section */}
            <section className="feature-section py-5 bg-dark">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <h2 className="mb-4">Unleash the Power of Steam Analytics</h2>
                            <p className="lead mb-4">
                                Steam offers a wealth of metrics across multiple categories. Our platform unites all these
                                analytics into a single, streamlined dashboard.
                            </p>
                            <ul className="feature-list">
                                <li>
                                    <FontAwesomeIcon icon={faBullhorn} className="me-2" />
                                    Identify What Drives Wishlists
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faRobot} className="me-2" />
                                    Amplify Your UTM Tracking
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faChartLine} className="me-2" />
                                    Relate Data Directly to Sales
                                </li>
                            </ul>
                            <Link className="btn btn-primary mt-3" to={Navigate.publishersOnboardingStep1Page()}>
                                Try It Free <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                            </Link>
                        </div>
                        <div className="col-lg-6 text-center">
                            <img
                                src="/assets/images/publishers/steam_analytics.gif"
                                alt="Steam Analytics Dashboard"
                                className="img-fluid rounded-3 shadow-lg border border-white border-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => openModal('/assets/images/publishers/steam_analytics.gif')}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Retention Metrics Section */}
            <section className="metrics-section py-5 bg-light">
                <div className="container text-black">
                    <h2 className="text-center mb-5 text-black">Track Retention & Active Users</h2>
                    <div className="row g-4">
                        <div className="col-md-6">
                            <div className="metric-card bg-white p-4 rounded-3 shadow-sm h-100">
                                <img
                                    src="/assets/images/publishers/charts_dau.png"
                                    className="img-fluid rounded-2 mb-3 border"
                                    alt="Active Users Chart"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => openModal('/assets/images/publishers/charts_dau.png')}
                                />
                                <h4 className="text-black">Effortlessly Track DAUs, WAUs & MAUs</h4>
                                <p className="mb-0">
                                    Check how many players engage with your game daily, weekly, and monthly, and watch how these
                                    figures evolve over time.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="metric-card bg-white p-4 rounded-3 shadow-sm h-100">
                                <img
                                    src="/assets/images/publishers/charts_retention.png"
                                    className="img-fluid rounded-2 mb-3 border"
                                    alt="Retention Chart"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => openModal('/assets/images/publishers/charts_retention.png')}
                                />
                                <h4 className="text-black">Analyze Retention</h4>
                                <p className="mb-0">
                                    Monitor Day 1, Day 7, Day 15, and Day 30 stats to pinpoint precisely where to refine your
                                    gameplay experience.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Marketing Funnel Section */}
            <section className="funnel-section py-5">
                <div className="container text-black">
                    <div className="row align-items-center">
                        <div className="col-lg-5 text-center mb-4 mb-lg-0">
                            <img
                                src="/assets/images/publishers/game_funnel.gif"
                                alt="Marketing Funnel"
                                className="img-fluid rounded-3 shadow-lg border border-white border-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => openModal('/assets/images/publishers/game_funnel.gif')}
                            />
                        </div>
                        <div className="col-lg-7">
                            <h2 className="mb-4 text-black">Analyze How Your Marketing Fuels Growth</h2>
                            <p className="lead mb-4">
                                Track every stage of your marketing funnel to understand how social awareness translates into
                                downloads and purchases.
                            </p>
                            <div className="funnel-features">
                                <div className="feature-item">
                                    <FontAwesomeIcon icon={faChartLine} className="text-primary me-3" />
                                    <div>
                                        <h5 className="mb-1 text-black">Campaign Performance</h5>
                                        <p className="small mb-0">See which campaigns drive the most conversions</p>
                                    </div>
                                </div>
                                <div className="feature-item mt-3">
                                    <FontAwesomeIcon icon={faUsers} className="text-success me-3" />
                                    <div>
                                        <h5 className="mb-1 text-black">Audience Insights</h5>
                                        <p className="small mb-0">Understand who engages with your content</p>
                                    </div>
                                </div>
                                <div className="feature-item mt-3">
                                    <FontAwesomeIcon icon={faBullhorn} className="text-danger me-3" />
                                    <div>
                                        <h5 className="mb-1 text-black">ROI Tracking</h5>
                                        <p className="small mb-0">Measure the return on your marketing spend</p>
                                    </div>
                                </div>
                            </div>
                            <Link className="btn btn-primary mt-4" to={Navigate.publishersOnboardingStep1Page()}>
                                Start Tracking Now
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Social Growth Section */}
            <section className="social-section py-5 bg-light">
                <div className="container text-black">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <h2 className="mb-4 text-black">Track Your Community Growth on Social Media</h2>
                            <p className="lead mb-4">
                                Keep tabs on your social media expansion as you cultivate a vibrant community of fans who
                                can't wait for your next update.
                            </p>
                            <ul className="social-features">
                                <li>
                                    <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                                    Follower growth tracking
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faChartLine} className="me-2 text-success" />
                                    Engagement rate analysis
                                </li>
                                <li>
                                    <FontAwesomeIcon icon={faBullhorn} className="me-2 text-danger" />
                                    Content performance metrics
                                </li>
                            </ul>
                            <Link className="btn btn-primary mt-3" to={Navigate.publishersOnboardingStep1Page()}>
                                Connect Your Social Accounts
                            </Link>
                        </div>
                        <div className="col-lg-6 text-center">
                            <img
                                src="/assets/images/publishers/social_growth.jpg"
                                alt="Social Media Growth"
                                className="img-fluid rounded-3 shadow-lg border border-white border-2"
                                style={{ cursor: 'pointer' }}
                                onClick={() => openModal('/assets/images/publishers/social_growth.jpg')}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="analytics-cta py-5">
                <div className="container text-center">
                    <h2 className="mb-4">Ready to Transform Your Game with Data?</h2>
                    <p className="cta-subtitle mb-5">Join thousands of developers making smarter decisions with Glitch Analytics</p>
                    <div className="cta-wrapper">
                        <Link
                            to={Navigate.publishersOnboardingStep1Page()}
                            className="btn btn-primary btn-lg px-5 py-3 me-3"
                        >
                            Get Started Free
                        </Link>
                        <Link
                            to={Navigate.demoPage()}
                            className="btn btn-outline-light btn-lg px-5 py-3"
                        >
                            Schedule Demo
                        </Link>
                    </div>
                </div>
            </section>

            <IndieSubmenu />
            <Footer />

            <style jsx>{`
                .analytics-hero {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
                    color: white;
                }

                .text-gradient {
                    background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .hero-visual {
                    position: relative;
                    border-radius: 20px;
                    overflow: hidden;
                    transform: perspective(1000px) rotateY(-5deg);
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .value-propositions {
                    background: #f8f9ff;
                }

                .value-card {
                    background: white;
                    padding: 2rem;
                    border-radius: 16px;
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    transition: transform 0.3s ease;
                }

                .value-card:hover {
                    transform: translateY(-10px);
                }

                .icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-bottom: 1.5rem;
                }

                .feature-section {
                    background: #080e37;
                    color: white;
                }

                .feature-list {
                    list-style: none;
                    padding-left: 0;
                }

                .feature-list li {
                    margin-bottom: 1rem;
                    font-size: 1.1rem;
                }

                .metrics-section {
                    background: #f8f9ff;
                }

                .metric-card {
                    transition: transform 0.3s ease;
                }

                .metric-card:hover {
                    transform: translateY(-5px);
                }

                .funnel-section {
                    background: white;
                }

                .funnel-features {
                    background: rgba(0,0,0,0.02);
                    padding: 1.5rem;
                    border-radius: 12px;
                }

                .feature-item {
                    display: flex;
                    align-items: flex-start;
                }

                .social-section {
                    background: #f8f9ff;
                }

                .social-features {
                    list-style: none;
                    padding-left: 0;
                }

                .social-features li {
                    margin-bottom: 0.75rem;
                    font-size: 1.1rem;
                }

                .analytics-cta {
                    padding: 100px 0;
                    background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
                    color: white;
                }

                @media (max-width: 768px) {
                    .analytics-hero {
                        padding: 60px 0;
                    }
                    
                    .hero-visual {
                        transform: none;
                    }
                }
            `}</style>
        </>
    );
}

export default PublisherAnalyticsPage;